/**
 * In-app tape recorder and player, one per player. Every restart starts a recording from that
 * respawn point, so a finished run can be kept with its inputs (the runs list, the best-time
 * ghost) and F9 can download the recording since the last restart; a playing tape feeds the
 * simulation instead of the devices (a replay, F10, the tests).
 */

import { type InputFrame, type Tape, TapeReader, TapeRecorder } from "@lache/sim";

/** Frames (input changes) a recording keeps before it stops growing: about an hour of play. */
export const MAX_RECORDED_FRAMES = 100_000;

export class Recorder {
  private recorder: TapeRecorder | null = null;
  private reader: TapeReader | null = null;
  lastTape: Tape | null = null;
  onPlaybackEnd: (() => void) | null = null;
  /** F9 was pressed: the next press downloads the recording (the telemetry says REC). */
  downloadArmed = false;

  get recording(): boolean {
    return this.recorder !== null;
  }

  get playing(): boolean {
    return this.reader !== null;
  }

  get playbackTick(): number {
    return this.reader ? this.reader.tick : 0;
  }

  get playbackTotal(): number {
    return this.reader ? this.reader.total : 0;
  }

  /** Ticks recorded since the last start. */
  get ticks(): number {
    return this.recorder ? this.recorder.ticks : 0;
  }

  start(course: string, tuningHash: string, respawn = 0): void {
    this.recorder = new TapeRecorder(course, tuningHash, 0, respawn);
  }

  /** Stop recording and return the tape. */
  stop(note?: string): Tape | null {
    if (!this.recorder) return null;
    const tape = this.recorder.finish(note);
    this.recorder = null;
    this.lastTape = tape;
    return tape;
  }

  /** Discard an untimed practice recording without replacing the last replayable tape. */
  discard(): void {
    this.recorder = null;
    this.downloadArmed = false;
  }

  /** A copy of the recording so far (the recording goes on), or null when not recording. */
  snapshot(note?: string): Tape | null {
    if (!this.recorder) return null;
    const tape = this.recorder.finish(note);
    return { ...tape, frames: tape.frames.map((f) => f.slice() as typeof f) };
  }

  /** Record one tick of live input. */
  push(input: InputFrame): void {
    if (!this.recorder || this.recorder.frames.length >= MAX_RECORDED_FRAMES) return;
    this.recorder.push(input);
  }

  play(tape: Tape): void {
    this.lastTape = tape;
    this.reader = new TapeReader(tape);
  }

  stopPlayback(): void {
    this.reader = null;
  }

  /** Fill `out` from the tape. Returns false (and ends playback) when exhausted. */
  next(out: InputFrame): boolean {
    if (!this.reader) return false;
    if (this.reader.next(out)) return true;
    this.reader = null;
    this.onPlaybackEnd?.();
    return false;
  }
}

export function downloadJson(name: string, data: unknown): void {
  const blob = new Blob([JSON.stringify(data)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
